import Page from '@classes/Page';
import PinSwapper from '@animations/PinSwapper.js';
import MobileCategorySwipe from '@animations/MobileCategorySwipe';
import GSAP from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ScrollToPlugin } from 'gsap/ScrollToPlugin';

GSAP.registerPlugin(ScrollTrigger, ScrollToPlugin);

export default class Menu extends Page {
  constructor(options) {
    super({
      element: '.menu',
      elements: {
        wrapper: '.menu__wrapper',
        section: '.menu__section',
        button: '.menu__category__label',
      },
    });

    this.lenis = options?.lenis ?? null;
  }

  create() {
    super.create();

    this.pinSwapper = new PinSwapper(this.lenis);

    // Hide all inactive sections immediately
    this.elements.section.forEach(section => {
      if (!section.classList.contains('--active')) {
        GSAP.set(section, { autoAlpha: 0 });
      } else {
        GSAP.set(section, { autoAlpha: 1 });
      }
    });

    // ALL buttons start grey — no black flash
    // Set initial button colors based on --active class in HTML
    GSAP.set(this.elements.button, { color: '#CBC4B1' }); // all grey first
    const activeBtn = Array.from(this.elements.button).find(btn =>
      btn.classList.contains('--active'),
    );
    if (activeBtn) {
      GSAP.set(activeBtn, { color: '#000', ease: 'power1.in' }); // then black for the active one
    }

    const categoryEl = document.querySelector('.menu__category');
    if (categoryEl) {
      new MobileCategorySwipe({ element: categoryEl });
    }

    const firstSection = document.querySelector('.menu__section.--active');
    if (firstSection) {
      this.pinSwapper.setupSection(firstSection);
    }

    this.elements.button.forEach(btn => {
      btn.addEventListener('click', () => {
        const category = btn.dataset.category;
        this.showCategory(category);
      });
    });
  }

  showCategory(category) {
    // Don't re-trigger if already active
    const alreadyActive = Array.from(this.elements.section).find(
      s => s.dataset.category === category && s.classList.contains('--active'),
    );
    if (alreadyActive) return;

    if (this.pinSwapper) {
      this.pinSwapper.destroy();
    }

    const activeSection = Array.from(this.elements.section).find(
      s => s.dataset.category === category,
    );
    const wrapper = activeSection
      ? activeSection.closest('.menu__wrapper') || activeSection
      : null;

    // Fade out all sections and strip --active
    this.elements.section.forEach(section => {
      section.classList.remove('--active');

      if (section !== activeSection) {
        GSAP.to(section, {
          autoAlpha: 0,
          duration: 0.4,
          ease: 'power2.out',
        });
      }
    });

    if (activeSection && wrapper) {
      activeSection.classList.add('--active');

      GSAP.fromTo(
        activeSection,
        { autoAlpha: 0 },
        {
          autoAlpha: 1,
          duration: 0.4,
          delay: 0.1,
          ease: 'power2.in',
        },
      );

      if (this.lenis?.scrollTo) {
        this.lenis.scrollTo(wrapper, {
          duration: 0,
          immediate: true,
          onComplete: () => {
            this.pinSwapper.setupSection(activeSection);
            ScrollTrigger.refresh(true);
          },
        });
      } else {
        this.pinSwapper.setupSection(activeSection);
        ScrollTrigger.refresh(true);
      }
    }

    // Update button states
    this.elements.button.forEach(btn => {
      const isActive = btn.dataset.category === category;
      btn.classList.toggle('--active', isActive);
      GSAP.to(btn, {
        color: isActive ? '#000' : '#CBC4B1',
        duration: 0.5,
        ease: 'expo.out',
      });
    });
  }
}
